// The office side of the customer renewal flow (Session AII, "Renewal B"): issue the link, see it,
// copy it, and see what the customer did with it.
//
// IT EXISTS BECAUSE THE REST OF THE FEATURE POINTS AT IT. The route that mints the token and the MCP
// tool that dispatches it both tell the office to "copy the link from the contract page", and the
// link is deliberately not returned by any MCP tool (it is a bearer credential for a page that needs
// no login, so anything that could read it could accept the renewal on the customer's behalf). That
// makes this panel the ONLY way the office can get the link, not a convenience.
//
// THIS PANEL SENDS (Session ANC, Director decision, 4 September 2026). It did not before, and the
// reversal is recorded here rather than quietly dropped, because a stale comment is part of how the
// incident behind it happened.
//
// WHAT IT USED TO SAY, and why it changed. Sessions AKP and ALB left this panel with "NOTHING HERE
// SENDS ANYTHING. Issuing creates the link; a human emails it." Issuing composed the invitation as a
// Gmail DRAFT and the office pressed Send in Gmail. A link was issued for a Rosina Street contract,
// the office believed the customer had it, and nobody had pressed Send. The Director's instruction
// is that this should work the way the Quotes page already does: a button that actually emails, and
// says plainly when it could not.
//
// IT IS NOT A REVERSAL OF THE PLATFORM AUTO-SEND RULE, and the next reader needs that distinction.
// That rule bans a customer email with NO HUMAN REVIEW STEP. A two-step office confirm naming the
// recipient IS the review step: CLAUDE.md lists human-pressed office buttons as deliberate
// non-violations, autoSendRule.test.ts's banned list does not contain this route, and
// callout.routes.ts says of the identical shape that "this is not a new exception, it is the
// identical one applied to the identical shape of action". What was reversed is the narrower AKP/ALB
// decision about THIS panel.
//
// COPY LINK STAYS, and is not a leftover. It is the fallback for the case the whole session is about:
// a send that did not go out, a customer with no billing email on record, or an address that needs
// correcting first. The office must always have a way to get the link by hand.
//
// ONCE THIS CYCLE HAS RENEWED THROUGH THE LEDGER (Session APB, Director decisions 1 and 2) the panel says
// so: the customer's page refuses the link until next year's window opens, and what the customer needs is
// the new charge. The send then composes the RENEWAL NOTICE (both figures, no link) rather than an
// invitation to a link that would be refused, and the panel shows any use of the link since the rise
// (each refused and recorded).
//
// THAT STATE IS ITS OWN PANEL, SHOWN FIRST (Session APB review). It used to sit inside the link view, behind
// the register gate and beside the link itself, so a contract priced only through the ledger (whose register
// was never confirmed) showed "confirm the lift register" and nothing else, and every fallback on the page
// still said to copy the link and send it, the one link the customer's page now refuses. In the renewed
// state there is no link, no Copy link and no Issue: only what renewed, whether the notice has gone, what is
// waiting in Gmail Drafts, and the one button that emails the notice.
import { useCallback, useEffect, useState } from "react";
import { api, ApiError, type ContractRenewalLink } from "../../lib/api";
import { buttonClasses } from "../../components/Button";
import { ConfirmButton } from "../../components/ConfirmButton";
import { formatDate, gbpExact } from "../../lib/format";
import { useSingleTimeout } from "../../lib/useSingleTimeout";

// Session ALB, Director decision: plain-English status for the invitation/confirmation, checked LIVE
// against Gmail on every load (never cached -- see the contract route's own comment). "unknown" reads
// honestly as "could not check" rather than defaulting to either the sent or the unsent state.
//
// SESSION ANC adds "sent" and rewords "not_drafted". "sent" is the platform's own send, so it names
// the day and hedges nothing: unlike "sent_or_removed", which infers a send from a draft's absence,
// this one is a record of an action this platform took. "not_drafted" stopped being a useful thing to
// tell the office the moment a send existed -- what they need to know is that the customer has not
// been contacted, not which internal mechanism did not fire -- so it now says exactly that.
function draftStateText(
  label: string,
  state: ContractRenewalLink["invitationDraftState"],
  sentAt?: string | null,
): string {
  switch (state) {
    case "sent": {
      const on = formatDate(sentAt);
      return on !== "-" ? `${label}: sent to the customer on ${on}.` : `${label}: sent to the customer.`;
    }
    case "drafted":
      return `${label}: drafted in Gmail, not yet sent.`;
    case "sent_or_removed":
      return `${label}: sent (no longer in Gmail Drafts).`;
    case "unknown":
      return `${label}: could not check right now (Gmail unreachable).`;
    case "not_drafted":
    default:
      return `${label}: not sent yet.`;
  }
}

// The customer-facing URL for a token. Built from the browser's own origin, so it is correct in
// every environment without a second place to configure a host (which is exactly how the retired
// onrender.com references got into the docs).
export function renewalLinkUrl(token: string): string {
  const origin = typeof window === "undefined" ? "" : window.location.origin;
  return `${origin}/renewal/${token}`;
}

// Session ANC. The send control for a link that already exists, and the two cases where there is
// deliberately nothing to press.
//
// THE CONFIRM NAMES THE ADDRESS. The platform's branded-confirm rule requires every outward customer
// action to name its recipient before it happens ("Send the quotation to name@site.com?"), and this
// is the office's one chance to notice the mail is about to go somewhere stale.
//
// AN ACTIONED LINK OFFERS NO SEND AT ALL. renewalActionedAt is a single-winner claim: the link has
// been used and the customer's own page now refuses it, so emailing it again would send a dead link
// to somebody who has already renewed. The route refuses this too; the button is hidden so the office
// is not invited to try. Copy link stays visible either way, because the office may still need the
// URL for their own records.
// SESSION AQG: WHAT BECAME OF THE ATTEMPT, in the office's own words.
//
// Until this session the panel said "Their new agreement is out for signature" for as long as the link
// stayed used, which stopped being true the moment the office voided that agreement, and stayed on the
// page for ever afterwards with no control of any kind beside it. The server now reports whether the
// paperwork is still live (renewalAttempt) and whether the link is spent, so this says the true thing
// in each state. An older server sends neither, and then the original sentence is still the best
// available reading.
export function usedLineText(link: ContractRenewalLink): string {
  const used = `The customer used this link on ${formatDate(link.actionedAt)}.`;
  // SPENT IS ASKED FIRST, and the order is the whole correctness of this sentence (Session AQG review).
  // A restart the customer has SINCE USED leaves restartedAt in place and EARLIER than the new claim, so
  // testing restartedAt first announced "so they can use it again" about a link their own page refuses,
  // buried the fact that an agreement was now out for signature, and read backwards into the bargain.
  if (!(link.spent ?? link.actionedAt != null) && link.restartedAt) {
    return `${used} You started this renewal again on ${formatDate(link.restartedAt)}, so they can use it again.`;
  }
  if (link.renewalAttempt && !link.renewalAttempt.live) {
    const named = link.renewalAttempt.quoteRef ? ` (${link.renewalAttempt.quoteRef})` : "";
    return `${used} The agreement it raised${named} is no longer live, so nothing is out for signature.`;
  }
  // A QUOTATION IS NOT AN AGREEMENT (Session AQG review). A tier-change renewal issues a real quotation
  // the customer has still to accept; saying an agreement is out for signature there tells the office a
  // document exists that nobody has raised, which is exactly what the customer's own view is careful not
  // to claim (toPublicRenewal's `quote_issued`). The quotation's reference is how the two are told apart:
  // only the tier-change shape has one.
  if (link.renewalAttempt?.quoteRef) {
    return `${used} The quotation it raised (${link.renewalAttempt.quoteRef}) is with them to accept.`;
  }
  return `${used} Their new agreement is out for signature.`;
}

function renderSendAction(link: ContractRenewalLink, busy: boolean, onSend: () => void) {
  // Session AQG: SPENT, not merely used. A renewal the office has started again is open once more, so
  // the invitation can go out for the same link. `spent` is the server's own answer (it is the rule the
  // send route refuses by), never a comparison this panel makes for itself; an older server that does
  // not send it falls back to the original meaning.
  if (link.spent ?? link.actionedAt != null) return null;
  if (!link.invitationRecipient) {
    return (
      <p className="mt-2 text-sm text-ele-body">
        No billing email is on record for this customer, so this cannot be emailed from here. Add one on their customer
        page, or copy the link below and send it yourself.
      </p>
    );
  }
  // A renewed cycle never reaches here: it has its own panel and its own button (the renewal notice).
  const already = link.invitationDraftState === "sent";
  return (
    <div className="mt-3">
      <ConfirmButton
        onConfirm={onSend}
        busy={busy}
        busyLabel="Sending..."
        label={already ? "Resend invitation" : "Send invitation"}
        confirmLabel={`Press again to email ${link.invitationRecipient}`}
        className={buttonClasses("primary", "md")}
        armedClassName="font-semibold ring-2 ring-ele-blue"
      />
    </div>
  );
}

// Session APB (Director decisions 1 and 2). This cycle's CPI rise has already been applied through the
// ledger: the agreement has renewed for the coming year and the customer's page refuses the link until the
// next cycle's window opens. Said in full here, figures and dates included, so the office never has to
// work out why the link will not open.
function renderRenewedCycle(link: ContractRenewalLink) {
  const r = link.alreadyRenewed;
  if (!r) return null;
  const unchanged = r.previousAnnual != null && r.previousAnnual === r.newAnnual;
  const change = unchanged
    ? ` left the annual charge unchanged at ${gbpExact(r.newAnnual ?? r.previousAnnual ?? 0)}`
    : r.previousAnnual != null && r.newAnnual != null
      ? ` took the annual charge from ${gbpExact(r.previousAnnual)} to ${gbpExact(r.newAnnual)}`
      : " has been applied";
  return (
    <div className="mt-2 rounded-ele bg-ele-surface p-3 text-sm text-ele-body">
      <p className="font-semibold text-ele-strong">This agreement has already renewed for the coming year.</p>
      <p className="mt-1">
        The CPI rise for the {formatDate(r.cycle)} review{change}
        {r.priceAppliedAt ? ` (applied ${formatDate(r.priceAppliedAt)})` : ""}. The customer&apos;s renewal link is refused until{" "}
        {formatDate(r.linkReopensOn)}, when next year&apos;s renewal opens
        {r.nextRenewalDate ? `, and the next renewal date is ${formatDate(r.nextRenewalDate)}` : ""}.
      </p>
      <p className="mt-1">
        {unchanged
          ? "There is no change to tell the customer. The renewal notice can still be emailed if they would like it in writing."
          : "To tell the customer the new charge, email them the renewal notice: it states both figures and carries no link. The renewal cycle check on this page says whether they have been sent it."}
      </p>
      {/* A customer who used the link BEFORE the rise was applied (it is refused after) may have a renewal of
          their own in flight, so the office checks it first. Said, never a block: the notice has no link. */}
      {link.actionedAt && (
        <p className="mt-1 text-ele-strong">
          The customer used their renewal link on {formatDate(link.actionedAt)}, before this rise was applied. Check where that
          renewal has got to before you send the notice.
        </p>
      )}
      {/* Every refused use since the rise is recorded (the page opened, or a choice submitted), at most one
          record every few minutes. An email security scanner opening the link is recorded the same way, so
          this is a prompt to check, never proof the customer tried. */}
      {link.lastRefusedAttemptAt && (
        <p role="alert" className="mt-1 text-ele-danger">
          Their renewal link was opened on {formatDate(link.lastRefusedAttemptAt)}, after the agreement had renewed, and the page
          told them it has already renewed. It may have been the customer, who may need a call, or an email security scanner.
        </p>
      )}
    </div>
  );
}

// Session APB review. Whether the renewal notice has gone, and what is waiting in Gmail Drafts, read live by
// the GET route: the platform's own record of emailing the notice (never the invitation's send stamp, which
// cannot tell the two apart), and the waiting draft's own subject.
function renewedNoticeStatus(r: NonNullable<ContractRenewalLink["alreadyRenewed"]>): string | null {
  if (r.noticeSentAt) return `Renewal notice: emailed to the customer on ${formatDate(r.noticeSentAt)}.`;
  switch (r.waitingDraft) {
    case "notice":
      return "Renewal notice: drafted in Gmail, not yet sent.";
    case "invitation":
      return "An older renewal invitation is still in Gmail Drafts. Discard it: it does not state the new charge, and its renewal link no longer works.";
    case "letter":
    case "other":
      return "A renewal email is waiting in Gmail Drafts. Check it before sending it.";
    case "unknown":
      return "Gmail could not be checked just now for a waiting draft.";
    default:
      return null;
  }
}

// Its own data load, following the register-confirmation panel beside it: a slow or failed fetch
// here never blocks the rest of the contract page, and the gate state comes from the same read as
// the token so the two can never disagree.
export function RenewalLinkPanel({ contractId }: { contractId: string }) {
  const [link, setLink] = useState<ContractRenewalLink | null>(null);
  const [loading, setLoading] = useState(true);
  // Session AKT. A SEPARATE busy flag from `loading`: the initial mount fetch hides the whole panel
  // while it runs (below), but a deliberate refresh must not -- the office is re-checking a link they
  // can already see, not waiting for the page to first appear.
  const [refreshing, setRefreshing] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const resetCopiedLater = useSingleTimeout();
  // Session ANC. The outcome of a send, held separately from `link` because the POST response is a
  // different shape from the GET's and folding it in would blank fields the GET owns. `ok: false` is
  // the state this session exists for: the link is live and the customer has NOT been contacted.
  const [sendResult, setSendResult] = useState<{ ok: boolean; text: string } | null>(null);
  // Session AQG: the reason the office gives for starting a spent renewal again.
  const [restartReason, setRestartReason] = useState("");

  const fetchLink = useCallback(async () => {
    try {
      setLink(await api.contracts.renewalLink(contractId));
    } catch {
      // A read failure must not break the contract page; the issue button still works.
      setLink(null);
    }
  }, [contractId]);

  useEffect(() => {
    setLoading(true);
    void fetchLink().finally(() => setLoading(false));
  }, [fetchLink]);

  // The manual "Refresh status" control (Session AKT): the office should never have to leave the
  // contract page, or ask someone else, to find out whether a link they are about to send -- or
  // already sent -- is currently live.
  const refresh = useCallback(async () => {
    setRefreshing(true);
    try {
      await fetchLink();
    } finally {
      setRefreshing(false);
    }
  }, [fetchLink]);

  // Session ANC: issue AND email in one press, matching the Quotes page's Send. `send: true` is what
  // separates this from the pre-ANC behaviour; nothing else about issuing changed, and the link is
  // still never rotated.
  //
  // THE RESPONSE IS READ, NOT DISCARDED, which is the same defect QuotesPage had to fix for Resend
  // (Session AIE): the route answers 200 with `invitationSent: false` when the link is live but the
  // email did not go out, so flashing a success here regardless would hand the office a positive
  // confirmation for a customer who has heard nothing. That is precisely the Rosina Street failure.
  //
  // A REFRESH FOLLOWS EVERY SEND. The POST response deliberately carries none of the GET's live state
  // (draft status, readiness, opened-at), so without re-reading, the status line under the button
  // would still say the invitation had not been sent.
  const issue = useCallback(
    async (send: boolean) => {
      setBusy(true);
      setError(null);
      setSendResult(null);
      // Session APB: the renewal notice, in a cycle that has already renewed through the ledger.
      const noticeEmail = link?.alreadyRenewed != null;
      try {
        const res = await api.contracts.issueRenewalLink(contractId, send ? { send: true } : undefined);
        if (send) {
          // Session APB: the notice is a different email from the invitation, so it is named as such, and
          // its fallbacks never say to send the link, which its customer's page now refuses.
          const notice = res.renewalNotice;
          if (res.invitationSent) {
            setSendResult({
              ok: true,
              text: notice
                ? notice.previousAnnual === notice.newAnnual
                  ? `Renewal notice sent to ${res.invitationRecipient ?? "the customer"}: it tells them the annual price review left the charge unchanged.`
                  : `Renewal notice sent to ${res.invitationRecipient ?? "the customer"}: it tells them the annual charge went from ${gbpExact(notice.previousAnnual)} to ${gbpExact(notice.newAnnual)}.`
                : `Renewal invitation ${res.invitationResent ? "resent" : "sent"} to ${res.invitationRecipient ?? "the customer"}.`,
            });
          } else if (!res.invitationRecipient) {
            setSendResult({
              ok: false,
              text: notice
                ? "This customer has no billing email on record, so nothing was sent. Add one on their customer page."
                : "This customer has no billing email on record, so nothing was sent. Add one on their customer page, or send the customer this link instead.",
            });
          } else {
            // The Quotes page's own wording for the same outcome, deliberately near-verbatim: it is
            // the right tone and the right honesty about failure, and the office should not have to
            // learn two vocabularies for one situation.
            setSendResult({
              ok: false,
              text: notice
                ? "The renewal notice could not be sent automatically. Nothing has reached the customer. Please try again shortly."
                : "The email could not be sent automatically. Send the customer this link instead.",
            });
          }
        } else if (!res.alreadyRenewed && !res.renewalNotice) {
          // An issue without send shows its link at once; the re-read below fills in the live state.
          setLink(res);
        }
        // Session APB review: A RE-READ FOLLOWS EVERY PRESS, not only a send. The POST response carries none
        // of the GET's live state (the renewed state, the waiting draft, readiness), and showing it in place
        // of the GET's used to drop the renewed block and show "send this link" for a link that is refused.
        await fetchLink();
      } catch (err) {
        setError(
          err instanceof ApiError && typeof err.code === "string" && err.code.includes(" ")
            ? err.code
            : noticeEmail
              ? "Could not send the renewal notice. Please try again."
              : send
                ? "Could not send the renewal invitation. Please try again."
                : "Could not issue the renewal link. Please try again.",
        );
      } finally {
        setBusy(false);
      }
    },
    [contractId, fetchLink, link?.alreadyRenewed],
  );

  // Session AQG: the office's reason for starting a spent renewal again, and the press itself. The
  // reason is required by the route (stored on the contract and audited), so the button stays disabled
  // until there is one. A refresh follows, exactly as every other press here does, because the GET owns
  // the live state this panel reads.
  const restart = useCallback(async () => {
    setBusy(true);
    setError(null);
    setSendResult(null);
    try {
      await api.contracts.restartRenewal(contractId, { reason: restartReason.trim() });
      setRestartReason("");
      setSendResult({ ok: true, text: "This renewal has been started again. The customer can use their existing link." });
      await fetchLink();
    } catch (err) {
      setError(
        err instanceof ApiError && typeof err.code === "string" && err.code.includes(" ")
          ? err.code
          : "Could not start this renewal again. Please try again.",
      );
    } finally {
      setBusy(false);
    }
  }, [contractId, fetchLink, restartReason]);

  // RULE 1's undo. Same shape as the restart: press, re-read, and the route's own refusal shown in
  // its own words when the customer got there first.
  const undoRestart = useCallback(async () => {
    setBusy(true);
    setError(null);
    setSendResult(null);
    try {
      await api.contracts.undoRenewalRestart(contractId);
      setSendResult({ ok: true, text: "The renewal link is closed again. Their earlier use still stands on the record." });
      await fetchLink();
    } catch (err) {
      setError(
        err instanceof ApiError && typeof err.code === "string" && err.code.includes(" ")
          ? err.code
          : "Could not undo this. Please try again.",
      );
    } finally {
      setBusy(false);
    }
  }, [contractId, fetchLink]);

  const copy = useCallback(async () => {
    if (!link?.token) return;
    try {
      await navigator.clipboard.writeText(renewalLinkUrl(link.token));
      setCopied(true);
      resetCopiedLater(() => setCopied(false), 2000);
    } catch {
      setError("Could not copy the link. Select it and copy by hand.");
    }
  }, [link, resetCopiedLater]);

  if (loading) return null;

  return (
    <section className="ele-card mt-4 p-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        {/* Session AQW: h2, matching RegisterConfirmationPanel and the other Renewal-tab cards --
            one heading level for every card title on the tab (was h3). */}
        <h2 className="font-heading text-lg text-ele-strong">Customer renewal link</h2>
        {/* Session AKT: always available, whatever state the panel is in, so the office never has to
            leave the contract page (or ask someone else) to find out whether a link is currently live. */}
        <button
          type="button"
          onClick={() => void refresh()}
          disabled={refreshing}
          className="rounded-ele border border-ele-hairline px-2 py-1 font-menu text-xs tracking-wide text-ele-body transition hover:opacity-90 disabled:opacity-50"
        >
          {refreshing ? "Refreshing..." : "Refresh status"}
        </button>
      </div>

      {link?.alreadyRenewed ? (
        <>
          {renderRenewedCycle(link)}
          {renewedNoticeStatus(link.alreadyRenewed) && (
            <p className="mt-2 text-sm text-ele-body">{renewedNoticeStatus(link.alreadyRenewed)}</p>
          )}
          {sendResult && (
            <p
              role={sendResult.ok ? "status" : "alert"}
              className={`mt-2 text-sm ${sendResult.ok ? "text-ele-green" : "text-ele-danger"}`}
            >
              {sendResult.text}
            </p>
          )}
          {error && (
            <p role="alert" className="mt-2 text-sm text-ele-danger">
              {error}
            </p>
          )}
          {link.invitationRecipient ? (
            <div className="mt-3">
              <ConfirmButton
                onConfirm={() => void issue(true)}
                busy={busy}
                busyLabel="Sending..."
                label="Email the renewal notice"
                confirmLabel={`Press again to email the renewal notice to ${link.invitationRecipient}`}
                className={buttonClasses("primary", "md")}
                armedClassName="font-semibold ring-2 ring-ele-blue"
              />
            </div>
          ) : (
            <p className="mt-2 text-sm text-ele-body">
              No billing email is on record for this customer, so the renewal notice cannot be emailed from here. Add one on their
              customer page.
            </p>
          )}
        </>
      ) : !link?.registerConfirmedAt ? (
        <p className="mt-2 text-sm text-ele-body">
          Confirm the lift register above before issuing a renewal link. The customer is priced on exactly the register you confirm.
        </p>
      ) : link?.token ? (
        <>
          {/* Live readiness (Session AKT), not just "a token was minted": a register confirmed at
              issue time is no guarantee it still prices -- a lift edited afterward can silently break
              it. Same reason text get_renewal_status gives the assistant, from the one shared constant
              (RENEWAL_BLOCKER_EXPLANATION), so the office is never told something different from chat. */}
          {link.notReadyReason && (
            <p role="alert" className="mt-2 text-sm text-ele-danger">
              This link currently will not work: {link.notReadyReason}
            </p>
          )}
          <p className="mt-2 text-sm text-ele-body">
            This link can only be used once. Send it to the customer below, or copy it and email it yourself.
          </p>
          {/* Session ALB, Director decision: whether the invitation was actually SENT, checked live
              against Gmail (never cached), so the office is never left wondering if the customer even
              has this link yet. Session ANC: a platform send answers this outright, with the date. */}
          <p className="mt-1 text-sm text-ele-body">{draftStateText("Invitation", link.invitationDraftState, link.invitationSentAt)}</p>

          {/* Session ANC. The send, and the honest report of what happened. The banner follows the two
              sibling renewal panels' role="status" / role="alert" pair rather than inventing a third
              convention for this page. */}
          {sendResult && (
            <p
              role={sendResult.ok ? "status" : "alert"}
              className={`mt-2 text-sm ${sendResult.ok ? "text-ele-green" : "text-ele-danger"}`}
            >
              {sendResult.text}
            </p>
          )}
          {error && (
            <p role="alert" className="mt-2 text-sm text-ele-danger">
              {error}
            </p>
          )}
          {renderSendAction(link, busy, () => void issue(true))}

          <div className="mt-2 flex flex-wrap items-center gap-2">
            <code className="ele-ref break-all rounded-ele bg-ele-surface px-2 py-1 text-xs">{renewalLinkUrl(link.token)}</code>
            <button
              type="button"
              onClick={() => void copy()}
              className="max-sm:min-h-11 rounded-ele border border-ele-hairline px-3 py-1.5 font-menu text-xs tracking-wide text-ele-strong transition hover:opacity-90"
            >
              {copied ? "Copied" : "Copy link"}
            </button>
          </div>
          {/* Session ALB: the same visibility a quote's own openedAt already gives the office, reused
              for a renewal link. A genuine stamp, not a guess: it can only ever be earlier than or
              equal to actionedAt/acceptedAt below. */}
          <p className="mt-2 text-sm text-ele-body">
            {link.renewalLinkOpenedAt ? `Link opened ${formatDate(link.renewalLinkOpenedAt)}.` : "Link not yet opened."}
          </p>
          <p className="mt-1 text-sm text-ele-body">
            {link.acceptedAt
              ? `The customer renewed on ${formatDate(link.acceptedAt)}.`
              : link.actionedAt
                ? usedLineText(link)
                : "Not used yet."}
          </p>
          {/* WHAT they agreed, not just that they did. Without this the office would have to open the
              customer's own link to find out, and on a tier change that link no longer shows it. */}
          {link.acceptedAt && link.acceptedTier && (
            <p className="mt-1 text-sm text-ele-strong">
              They chose <b>{link.acceptedTier}</b> cover
              {link.acceptedFrequency ? `, ${link.acceptedFrequency} visits` : ""}
              {link.acceptedAnnual == null ? "" : `, at £${Math.round(link.acceptedAnnual).toLocaleString("en-GB")} a year plus VAT`}.
            </p>
          )}
          {/* Only exists once accepted: nothing is drafted before then. */}
          {link.acceptedAt && (
            <p className="mt-1 text-sm text-ele-body">{draftStateText("Confirmation", link.confirmationDraftState)}</p>
          )}
          {/* The office's own reason, which only the audit log held until the review pointed out that no
              office surface reads it. */}
          {link.restartedAt && link.restartReason && (
            <p className="mt-1 text-sm text-ele-body">Reason given: {link.restartReason}</p>
          )}
          {/* RULE 1: the way back from a restart, while it is still the office's own decision to unmake.
              Two-step like every consequential control here, and it disappears the moment the customer
              uses the reopened link, which the route refuses too. */}
          {link.restartedAt && !link.acceptedAt && !(link.spent ?? true) && (
            <div className="mt-2">
              <ConfirmButton
                onConfirm={undoRestart}
                busy={busy}
                busyLabel="Undoing..."
                label="Undo, close the link again"
                confirmLabel="Press again to close the link"
                className="max-sm:min-h-11 rounded-ele border border-ele-line px-3 py-1.5 font-menu text-xs tracking-wide text-ele-body transition hover:opacity-90 disabled:opacity-50"
              />
            </div>
          )}
          {/* SESSION AQG: START THIS RENEWAL AGAIN. Offered only where it can work -- the link is spent,
              nothing is signed, and the agreement it raised is no longer live -- because the route
              refuses every other case and a button that always refuses is worse than none. A reason is
              required (it is stored on the contract and audited), and the press is the platform's
              two-step branded confirm, since this hands a customer a second chance to renew. */}
          {(link.spent ?? link.actionedAt != null) && !link.acceptedAt && link.renewalAttempt && !link.renewalAttempt.live && (
            <div className="mt-3 border-t border-ele-line pt-3">
              <label className="block font-menu text-xs uppercase tracking-wide text-ele-muted" htmlFor="renewal-restart-reason">
                Start this renewal again
              </label>
              <p className="mt-1 text-sm text-ele-body">
                The customer can use their existing link again. Their earlier use stays on the record.
              </p>
              <input
                id="renewal-restart-reason"
                type="text"
                value={restartReason}
                onChange={(e) => setRestartReason(e.target.value)}
                placeholder="Why, in a few words (e.g. voided the agreement: it renewed the wrong year)"
                maxLength={200}
                className="ele-input mt-2 w-full text-sm"
              />
              <div className="mt-2">
                <ConfirmButton
                  onConfirm={restart}
                  busy={busy}
                  busyLabel="Starting..."
                  disabled={restartReason.trim().length < 3}
                  label="Start this renewal again"
                  confirmLabel="Press again to reopen the customer's renewal link"
                  className="max-sm:min-h-11 rounded-ele border border-ele-purple px-4 py-2 font-menu text-xs tracking-wide text-ele-strong transition hover:opacity-90 disabled:opacity-50"
                  armedClassName="font-semibold ring-2 ring-ele-blue"
                />
              </div>
            </div>
          )}
        </>
      ) : (
        <>
          <p className="mt-2 text-sm text-ele-body">
            Creates a one-time link the customer uses to choose their cover level and visit frequency for the year ahead. Their
            lifts stay exactly as you confirmed them.
            {link?.invitationRecipient
              ? ` Issuing emails the link straight to ${link.invitationRecipient}, and tells you here if it could not go out.`
              : " This customer has no billing email on record, so the link is created for you to copy and send yourself."}
          </p>
          {sendResult && (
            <p
              role={sendResult.ok ? "status" : "alert"}
              className={`mt-2 text-sm ${sendResult.ok ? "text-ele-green" : "text-ele-danger"}`}
            >
              {sendResult.text}
            </p>
          )}
          {error && (
            <p role="alert" className="mt-2 text-sm text-ele-danger">
              {error}
            </p>
          )}
          {/* Session ANC: ONE press does both, which is what the Director asked for. The confirm names
              the address, so the send is never a surprise. With no billing email on record there is
              nothing to name and nothing to send, so the button honestly offers only the link. */}
          <div className="mt-3">
            <ConfirmButton
              onConfirm={() => void issue(Boolean(link?.invitationRecipient))}
              busy={busy}
              busyLabel={link?.invitationRecipient ? "Sending..." : "Issuing..."}
              label={link?.invitationRecipient ? "Issue and send renewal link" : "Issue renewal link"}
              confirmLabel={link?.invitationRecipient ? `Press again to email ${link.invitationRecipient}` : "Press again to issue"}
              className={buttonClasses("primary", "md")}
              armedClassName="font-semibold ring-2 ring-ele-blue"
            />
          </div>
        </>
      )}
    </section>
  );
}
